import type { ReactElement, ReactNode } from 'react';
import { useEffect, useState, forwardRef } from 'react';
import { useIntl } from 'react-intl';
import {
  Select as AriaSelect,
  type SelectProps as AriaSelectProps,
  Autocomplete,
  ListBox,
  Popover,
  type PopoverProps as AriaPopoverProps,
  SelectValue,
  type ValidationResult,
  useFilter,
  ButtonContext,
  type Key,
} from 'react-aria-components';
import cx from 'clsx';
import { isNil } from 'es-toolkit';
import {
  IconChevronBottomOutlined,
  IconChevronTopOutlined,
  IconInfoRoundedOutlined,
} from '@repo/monochrome-icons';
import { CloseCircleFilled } from '../../assets/icons';
import { SearchFieldWithDebounce } from '../search-field-with-debounce';
import { SearchField } from '../search-field';
import {
  Description,
  Error,
  Label,
  Option,
  OptionDivider,
  OptionGroup,
  Trigger,
} from '../form-elements';
import { Tooltip } from '../tooltip/tooltip';
import styles from './select.module.css';

export interface SelectProps<T extends object> extends AriaSelectProps<T> {
  label?: string;
  labelTooltip?: string;
  description?: string | ReactNode;
  errorMessage?: string | ((validation: ValidationResult) => string);
  placeholder?: string;
  multiple?: boolean;
  inputClassName?: string;
  isOptional?: boolean;
  isOptionalText?: string;
  isReadOnly?: boolean;
  isDisabled?: boolean;
  fullWidth?: boolean;
  selectedValueClassName?: string;
  customSelectedValueContent?: ReactNode;
  displaySelectedTextValue?: boolean;
  allowClear?: boolean;
  withSearch?: {
    async?: boolean;
    placeholder: string;
    emptyMessage?: string;
    loadingMessage?: string;
    autoFocus?: boolean;
  };
  isLoading?: boolean;
  autocompleteInputValue?: string;
  autocompleteOnInputChange?: (value: string) => void;
  portalContainerId?: string;
  optionListClassName?: string;
  popoverPlacement?: AriaPopoverProps['placement'];
  popoverClass?: string;
}

const SelectComponent = forwardRef(function SelectComponent<T extends object>(
  {
    label,
    labelTooltip,
    description,
    errorMessage,
    isOptional,
    isOptionalText,
    children,
    defaultOpen = false,
    isOpen,
    onOpenChange,
    inputClassName,
    isDisabled,
    isReadOnly,
    fullWidth = true,
    selectedValueClassName,
    customSelectedValueContent,
    displaySelectedTextValue,
    allowClear = false,
    withSearch,
    isLoading = false,
    portalContainerId,
    placeholder,
    autocompleteInputValue,
    autocompleteOnInputChange,
    optionListClassName,
    popoverPlacement = 'bottom start',
    popoverClass,
    ...props
  }: SelectProps<T>,
  ref: React.ForwardedRef<HTMLDivElement>
): ReactElement {
  const intl = useIntl();
  // eslint-disable-next-line @typescript-eslint/unbound-method -- Not sure why eslint complains here
  const { contains } = useFilter({ sensitivity: 'base' });
  const [internalIsOpen, setInternalIsOpen] = useState(defaultOpen);

  function handleOpenChange(open: boolean): void {
    setInternalIsOpen(open);
    onOpenChange?.(open);
  }

  useEffect(() => {
    if (!isNil(isOpen)) {
      setInternalIsOpen(isOpen);
    }
  }, [isOpen]);

  // react-aria modal escapes the test container, we render it where it can find the modal (i.e. <div> component with 'ember-testing' id)
  const TEST_PORTAL_CONTAINER =
    ((portalContainerId ? document.getElementById(portalContainerId) : undefined) ||
      document.getElementById('ember-testing')) ??
    undefined;

  const autocompleteProps = withSearch?.async
    ? {
        inputValue: autocompleteInputValue,
        onInputChange: autocompleteOnInputChange,
      }
    : {
        filter: contains,
      };

  const SearchComponent = withSearch?.async ? SearchFieldWithDebounce : SearchField;

  const handleClear = (e: React.MouseEvent | React.KeyboardEvent | React.PointerEvent): void => {
    e.preventDefault();
    e.stopPropagation();

    if (props.onSelectionChange) {
      props.onSelectionChange(null as unknown as Key);
    }
  };

  const showClearButton = allowClear && props.selectedKey && !isDisabled && !isReadOnly;

  return (
    <AriaSelect
      {...props}
      data-readonly={String(Boolean(isReadOnly) && !isDisabled)}
      isDisabled={isDisabled || isReadOnly}
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
      placeholder={placeholder}
      ref={ref}
    >
      <div className={styles.container}>
        {Boolean(label) && (
          <div className={styles.labelContainer}>
            <Label>
              {label} {Boolean(isOptional) && isOptionalText}
            </Label>
            {Boolean(labelTooltip) && (
              <ButtonContext.Provider value={null}>
                <Tooltip
                  buttonProps={{
                    'aria-label': labelTooltip,
                  }}
                  label={labelTooltip}
                  needsButton
                  placement="top left"
                >
                  <IconInfoRoundedOutlined className={styles.tooltipIcon} />
                </Tooltip>
              </ButtonContext.Provider>
            )}
          </div>
        )}
        <Trigger
          className={cx(styles['input-container'], inputClassName)}
          // override the readonly/disabled state defined for the AriaSelect component
          isDisabled={isDisabled ?? false}
          isReadOnly={isReadOnly}
        >
          {customSelectedValueContent ?? (
            <SelectValue className={cx(styles['input-value'], selectedValueClassName)}>
              {displaySelectedTextValue
                ? ({ selectedText, defaultChildren }) => {
                    return selectedText || defaultChildren;
                  }
                : undefined}
            </SelectValue>
          )}
          {Boolean(showClearButton) && (
            <CloseCircleFilled
              aria-label={intl.formatMessage({ id: 'datepicker-btn-clear' })}
              className={styles.clearButton}
              onPointerDown={e => {
                handleClear(e);
              }}
              role="button"
              tabIndex={0}
            />
          )}
          {Boolean(!showClearButton && internalIsOpen) && <IconChevronTopOutlined aria-hidden />}
          {!showClearButton && !internalIsOpen && (
            <IconChevronBottomOutlined
              aria-hidden
              className={cx(isReadOnly && styles['chevron-icon--readonly'])}
            />
          )}
        </Trigger>
        <Error>{errorMessage}</Error>
        {Boolean(description) && (
          <Description data-testid="select-description">{description}</Description>
        )}
      </div>
      <Popover
        UNSTABLE_portalContainer={TEST_PORTAL_CONTAINER}
        className={cx(
          'body-standard',
          styles.popover,
          fullWidth && styles['full-width'],
          popoverClass
        )}
        offset={8}
        placement={popoverPlacement}
      >
        {withSearch ? (
          <Autocomplete {...autocompleteProps}>
            <SearchComponent
              // oxlint-disable-next-line jsx-a11y/no-autofocus -- only when explicitly enabled for a component
              autoFocus={withSearch.autoFocus}
              inputClassName={styles.search}
              placeholder={withSearch.placeholder}
            />
            <ListBox
              className={cx(styles['option-list'], optionListClassName)}
              renderEmptyState={() => {
                if (isLoading && withSearch.loadingMessage) {
                  return <div className={styles['no-results']}>{withSearch.loadingMessage}</div>;
                }
                if (withSearch.emptyMessage) {
                  return <div className={styles['no-results']}>{withSearch.emptyMessage}</div>;
                }
                return null;
              }}
            >
              {children}
            </ListBox>
          </Autocomplete>
        ) : (
          <ListBox className={cx(styles['option-list'], optionListClassName)}>{children}</ListBox>
        )}
      </Popover>
    </AriaSelect>
  );
}) as <T extends object>(
  props: SelectProps<T> & { ref?: React.ForwardedRef<HTMLDivElement> }
) => ReactElement;

export const Select = Object.assign(SelectComponent, {
  Option,
  Divider: OptionDivider,
  Group: OptionGroup,
});
